import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as IconCalendar,
  MoreHorizontal,
  LayoutGrid,
  AlignJustify,
  Eye,
} from 'lucide-react';
import { useisUserAdmin } from '../../../config/unificacion';
import { useUsers } from '../../../metodos/userContext';


const parseFechaLocal = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  const d = new Date(v);
  return isNaN(d) ? null : d;
};
const atStartOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

const atEndOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const startOfWeekSunday = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const endOfWeekSaturday = (d) => {
  const s = startOfWeekSunday(d);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};

const monthGridStart = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  first.setHours(0, 0, 0, 0);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return start;
};
const monthGridEnd = (d) => {
  const start = monthGridStart(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 41); // 6x7 - 1
  end.setHours(23, 59, 59, 999);
  return end;
};

const obtenerLogoEmpresa = (nombreEmpresa) => {
  if (!nombreEmpresa)
    return `https://ui-avatars.com/api/?name=?&background=dcdcdc&color=fff`;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    nombreEmpresa
  )}&background=random&color=fff&font-size=0.5`;
};

const determinarColorInfo = (isAdmin, identificadorcodigo, servicioEntregado) => {
  const colores = {
    orange: { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-500' },
    red: { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-500' },
    green: { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-500' },
    blue: { bg: 'bg-blue-600', border: 'border-blue-600', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-600', border: 'border-purple-600', text: 'text-purple-600' },
  };
  if (identificadorcodigo === 'P' || identificadorcodigo === 'Q') {
    return colores.orange;
  }else if (!servicioEntregado ) {
    return colores.red;
  }else if(servicioEntregado){
    return colores.green;
  }else{
    return colores.blue;
  }
};

const obtenerHora = (fecha, identificadorcodigo,codigo = "") => {
  console.log("obtener hora",fecha,identificadorcodigo,codigo);
  const dateObj = parseFechaLocal(fecha);
  if (!dateObj) return { inicio: 9, fin: 10 };
  const hora = dateObj.getHours();
  if (identificadorcodigo === 'T' || identificadorcodigo === 'TES')
    return { inicio: Math.max(0, hora - 1), fin: Math.max(1, hora) };
  return { inicio: hora, fin: Math.min(24, hora + 2) };
};

/* =============================
   CalendarioWidget
   ============================= */

const CalendarioWidget = forwardRef(function CalendarioWidget(
  {
    servicios = [],
    onServicioClick,
    onRangoChange, // (payload: { fechaInicio, fechaFinal, vista })
    /* Props de eco desde el padre (opcionales) */
    fechaInicio,
    fechaFinal,
  },
  ref
) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [vista, setVista] = useState('mes');
  const [subVistaSemana, setSubVistaSemana] = useState('calendario');
  const [subVistaDia, setSubVistaDia] = useState('calendario');

  const { usuario } = useUsers();
  const isAdmin = useisUserAdmin(usuario);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  const diasSemana = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
  const diasSemanaUnaLetra = ['D','L','M','X','J','V','S'];

  /* ========= Eventos ========= */
  const eventosDelCalendario = useMemo(() => {
    const out = [];
    servicios.forEach((servicio) => {
      const f = parseFechaLocal(servicio.fechaentrega);
      if (!f) return;
      const horas = obtenerHora(f, servicio.identificadorcodigo);
      out.push({
        id: `PRINCIPAL-${servicio.codigo}`,
        tipo: 'servicioPrincipal',
        fecha: f,
        titulo: servicio.materia || 'Servicio sin Título',
        horaInicio: horas.inicio,
        horaFin: horas.fin,
        colorInfo: determinarColorInfo(isAdmin, servicio.identificadorcodigo, servicio.getTrabajoEntregado()),
        logoUrl: obtenerLogoEmpresa(servicio.nombreEmpresa),
        ...servicio,
        isUnad: false,
      });
      (servicio.entregasTrabajo || []).forEach((entrega) => {
        const fp = parseFechaLocal(entrega?.fechaentrega);
        if (!fp) return;
        const hp = obtenerHora(fp, servicio.identificadorcodigo, servicio.codigo);
        out.push({
          id: `ENTREGA-${servicio.codigo}-${entrega?.id || fp.getTime()}`,
          tipo: 'entregaParcial',
          fecha: fp,
          titulo: entrega?.id || 'Entrega',
          horaInicio: hp.inicio,
          horaFin: hp.fin,
          logoUrl: obtenerLogoEmpresa(servicio.nombreEmpresa),
          colorInfo: determinarColorInfo(isAdmin, 'ENTREGA', entrega.getTrabajoEntregado()),
          ...entrega,
          ...servicio,
          isUnad: false,
        });
      });
    });
    return out;
  }, [servicios, isAdmin]);

  /* ========= Rango visible ========= */
  const getRangoVisible = () => {
    if (vista === 'mes') {
      return {
        fechaInicio: monthGridStart(currentDate),
        fechaFinal: monthGridEnd(currentDate),
        vista,
      };
    }
    if (vista === 'semana') {
      return {
        fechaInicio: startOfWeekSunday(currentDate),
        fechaFinal: endOfWeekSaturday(currentDate),
        vista,
      };
    }
    return {
      fechaInicio: atStartOfDay(currentDate),
      fechaFinal: atEndOfDay(currentDate),
      vista,
    };
  };

  useEffect(() => {
    onRangoChange && onRangoChange(getRangoVisible());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, vista]);

  useImperativeHandle(ref, () => ({
    getRangoVisible,
  }));

  /* ========= Navegación ========= */
  const handleNavigate = (dir) => {
    const nueva = new Date(currentDate);
    if (vista === 'dia') nueva.setDate(nueva.getDate() + dir);
    else if (vista === 'semana') nueva.setDate(nueva.getDate() + dir * 7);
    else nueva.setMonth(nueva.getMonth() + dir);
    setCurrentDate(nueva);
  };
  const irAHoy = () => setCurrentDate(new Date());

  /* ========= UX selección ========= */
  const [seleccion, setSeleccion] = useState(null); // {date, hour?}
  const limpiarSeleccion = () => setSeleccion(null);

  /* ========= Header ========= */
  const CalendarHeader = () => {
    const formatHeaderDate = () => {
      if (vista === 'dia')
        return `${currentDate.getDate().toString().padStart(2, '0')} ${meses[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
      if (vista === 'semana') {
        const inicio = startOfWeekSunday(currentDate);
        const fin = endOfWeekSaturday(currentDate);
        return `${inicio.getDate().toString().padStart(2, '0')} ${meses[inicio.getMonth()].substring(0, 3)} - ${fin.getDate().toString().padStart(2, '0')} ${meses[fin.getMonth()].substring(0, 3)}, ${fin.getFullYear()}`;
      }
      return `${meses[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    };
    const activeSubVista = vista === 'semana' ? subVistaSemana : subVistaDia;
    const setSubVista = (sv) => {
      if (vista === 'semana') setSubVistaSemana(sv);
      if (vista === 'dia') setSubVistaDia(sv);
    };

    return (
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center p-4 border-b border-gray-200 gap-4">
          <h2 className="text-lg text-gray-800 flex items-center shrink-0">
            <span className="font-bold text-black mr-2">Calendario:</span>
            <span className="text-blue-600 font-medium capitalize">{`Vista ${vista}`}</span>
          </h2>

          <div className="flex items-center bg-gray-100 rounded-full p-1 space-x-2 w-full lg:w-auto justify-center">
            <button onClick={() => handleNavigate(-1)} className="p-1.5 text-gray-600 hover:bg-gray-200 rounded-full">
              <ChevronLeft size={20} />
            </button>
            <span className="flex-1 lg:flex-none lg:w-48 text-center font-semibold text-gray-800 text-sm">
              {formatHeaderDate()}
            </span>
            <button onClick={() => handleNavigate(1)} className="p-1.5 text-gray-600 hover:bg-gray-200 rounded-full">
              <ChevronRight size={20} />
            </button>
            <button onClick={irAHoy} className="p-1.5 text-gray-600 hover:bg-gray-200 rounded-full">
              <IconCalendar size={18} />
            </button>
          </div>

          <div className="flex items-center space-x-2 justify-center w-full lg:w-auto">
            {(vista === 'semana' || vista === 'dia') && (
              <div className="flex items-center bg-gray-200 rounded-full p-1">
                <button
                  onClick={() => setSubVista('calendario')}
                  className={`p-1.5 rounded-full ${activeSubVista === 'calendario' ? 'bg-white text-blue-600 shadow' : 'text-gray-500'}`}
                >
                  <LayoutGrid size={20} />
                </button>
                <button
                  onClick={() => setSubVista('lista')}
                  className={`p-1.5 rounded-full ${activeSubVista === 'lista' ? 'bg-white text-blue-600 shadow' : 'text-gray-500'}`}
                >
                  <AlignJustify size={20} />
                </button>
              </div>
            )}
            <select
              value={vista}
              onChange={(e) => setVista(e.target.value)}
              className="bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-full border-0 focus:ring-2 focus:ring-blue-400 focus:outline-none appearance-none text-sm cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.75rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1em 1em',
                paddingRight: '2.5rem',
              }}
            >
              <option value="mes">mes</option>
              <option value="semana">semana</option>
              <option value="dia">día</option>
            </select>
          </div>
        </div>

        {/* Info rápida del día actual */}
        <div className="px-4 py-2 border-b border-gray-100 bg-blue-50/40">
          <div className="flex items-center gap-3 text-sm">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold">
              {now.getDate().toString().padStart(2, '0')}
            </span>
            <div className="text-gray-700">
              <p className="font-semibold capitalize">{`${diasSemana[now.getDay()]} ${now.getDate()} de ${meses[now.getMonth()]} de ${now.getFullYear()}`}</p>
              <p className="text-xs opacity-80">
                Hoy tienes {eventosDelCalendario.filter((e) => e.fecha.toDateString() === now.toDateString()).length} evento(s).
              </p>
            </div>
          </div>
        </div>

        {/* (Opcional) Mostrar las props fechaInicio/fechaFinal que vienen del padre */}
        {(fechaInicio || fechaFinal) && (
          <div className="px-4 py-1 border-b text-xs text-gray-500 bg-gray-50">
            Rango desde el padre → {fechaInicio ? new Date(fechaInicio).toLocaleDateString() : '—'} &rarr; {fechaFinal ? new Date(fechaFinal).toLocaleDateString() : '—'}
          </div>
        )}
      </div>
    );
  };

  /* ========= Vista Card ========= */
  const vistaCard = (evento,isFullCard=false) =>{
    return(
      <div
        key={evento.id}
        className={`${evento.colorInfo.bg} text-white text-xs font-medium p-1 rounded w-full truncate cursor-pointer`}
        onClick={(e) => {
          e.stopPropagation();
          onServicioClick && onServicioClick(evento);
        }}
        title={`${evento.titulo} - ${evento.codigo} `}
      >
        <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
          (UNAD) {evento.titulo} - {evento.codigo}
        </span>
      </div>
    );
  }

  /* ========= VISTA MES ========= */
  const VistaMes = () => {
    const año = currentDate.getFullYear();
    const mes = currentDate.getMonth();
    const dias = Array.from({ length: 42 }, (_, i) => {
      const primerDiaMes = new Date(año, mes, 1).getDay();
      return new Date(año, mes, i - primerDiaMes + 1);
    });

    return (
      <div>
        <div className="hidden lg:grid grid-cols-7">
          <div className="grid grid-cols-7 col-span-7">
            {['domingo','lunes','martes','miércoles','jueves','viernes','sábado'].map((dia) => (
              <div key={dia} className="text-center p-2 border-b border-r border-gray-200 text-sm capitalize text-gray-600 font-medium">
                {dia}
              </div>
            ))}
          </div>
          {dias.map((fecha, i) => {
            const eventosDelDia = eventosDelCalendario.filter(
              (e) => e.fecha.toDateString() === fecha.toDateString()
            );
            const esMesActual = fecha.getMonth() === mes;
            const esHoy = fecha.toDateString() === now.toDateString();

            return (
              <div
                key={i}
                className={`h-32 border-b border-r border-gray-200 p-1.5 space-y-1 overflow-hidden ${
                  !esMesActual ? 'bg-gray-50' : 'bg-white'
                }`}
                onClick={() => setCurrentDate(fecha)}
              >
                <div className={`text-xs font-semibold flex justify-end ${!esMesActual ? 'text-gray-400' : 'text-gray-700'}`}>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full ${esHoy ? 'bg-blue-600 text-white' : ''}`}>
                    {fecha.getDate()}
                  </span>
                </div>

                {/* Eventos del mes */}
                <div className="space-y-1">
                  {/* Eventos del dia <=2 */}
                  {eventosDelDia.length <= 2 &&
                    eventosDelDia.map((evento) => (
                      vistaCard(evento)
                    ))}

                  {/* Eventos del dia > 2 */}
                  {eventosDelDia.length > 2 && (
                    <>
                      {eventosDelDia.slice(0, 1).map((evento) => (
                        vistaCard(evento)
                      ))}
                      <div
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-1 cursor-pointer flex items-center justify-center rounded w-full text-xs"
                        onClick={() => {
                          setVista('dia');
                          setCurrentDate(fecha);
                        }}
                        title={`Ver ${eventosDelDia.length - 1} más`}
                      >
                        <MoreHorizontal size={14} className="mr-1" /> {eventosDelDia.length - 1} más
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Móvil */}
        <div className="block lg:hidden p-2">
          <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-500 mb-2">
            {diasSemanaUnaLetra.map((dia) => <div key={dia}>{dia}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {dias.map((fecha, i) => {
              const eventosDelDia = eventosDelCalendario.filter((e) => e.fecha.toDateString() === fecha.toDateString());
              const esMesActual = fecha.getMonth() === mes;
              const esHoy = fecha.toDateString() === now.toDateString();
              return (
                <div
                  key={i}
                  className="aspect-square flex flex-col items-center justify-center cursor-pointer rounded-lg transition-colors duration-200 hover:bg-gray-100"
                  onClick={() => setCurrentDate(fecha)}
                >
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-sm ${
                      !esMesActual ? 'text-gray-300' : 'text-gray-800'
                    } ${esHoy ? 'bg-blue-600 text-white font-bold' : ''}`}
                  >
                    {fecha.getDate()}
                  </span>
                  {eventosDelDia.length > 0 && (
                    <div className={`w-1.5 h-1.5 ${esHoy ? 'bg-white' : 'bg-red-500'} rounded-full mt-1`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  /* ========= VISTA SEMANA ========= */
  const VistaSemana = () =>
    subVistaSemana === 'lista' ? <VistaSemanaLista /> : <VistaSemanaCalendario />;

  const VistaSemanaLista = () => {
    const semana = Array.from({ length: 7 }, (_, i) => {
      const fecha = new Date(currentDate);
      fecha.setDate(currentDate.getDate() - currentDate.getDay() + i);
      return fecha;
    });

    return (
      <div className="divide-y divide-gray-200 lg:divide-y-0 lg:grid lg:grid-cols-7 lg:divide-x">
        {semana.map((fecha) => {
          const eventos = eventosDelCalendario
            .filter((e) => e.fecha.toDateString() === fecha.toDateString())
            .sort((a, b) => a.horaInicio - b.horaInicio);

          const esHoy = fecha.toDateString() === now.toDateString();

          return (
            <div key={fecha.toISOString()} className={`p-2 lg:p-0 ${esHoy ? 'bg-blue-50/50' : ''}`} onClick={() => setCurrentDate(fecha)}>
              <div className={`flex items-center gap-2 lg:justify-center lg:text-center p-2 border-b lg:border-r ${esHoy ? 'text-blue-600' : 'text-gray-800'}`}>
                <span className="font-bold text-lg lg:text-base">{fecha.getDate()}</span>
                <span className="font-semibold text-sm capitalize">{['domingo','lunes','martes','miércoles','jueves','viernes','sábado'][fecha.getDay()]}</span>
              </div>
              <div className="space-y-2 p-2">
                {eventos.map((evento) => (
                  <div
                    key={evento.id}
                    className={`${evento.colorInfo.bg} text-white rounded-lg p-2 text-xs cursor-pointer flex flex-col overflow-hidden`}
                    onClick={(e) => { e.stopPropagation(); onServicioClick && onServicioClick(evento); }}
                    title={`${evento.titulo} (${evento.cliente || ''})`}
                  >
                    <p className="opacity-75 truncate">{evento.codigo}</p>
                    <p className="font-semibold text-sm truncate">{evento.titulo}</p>
                    <p className="opacity-75">{`${evento.horaInicio.toString().padStart(2, '0')}:00 - ${evento.horaFin.toString().padStart(2, '0')}:00`}</p>
                  </div>
                ))}
                {eventos.length === 0 && <p className="text-center text-xs text-gray-400 py-4 lg:hidden">Sin eventos</p>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const VistaSemanaCalendario = () => {
    const semana = Array.from({ length: 7 }, (_, i) => {
      const fecha = new Date(currentDate);
      fecha.setDate(currentDate.getDate() - currentDate.getDay() + i);
      return fecha;
    });

    const ALTURA_HORA_PX = 36;
    const horas = Array.from({ length: 24 }, (_, i) => i);
    const hoyIndex = semana.findIndex((d) => d.toDateString() === now.toDateString());
    const posicionHoraActual = (now.getHours() + now.getMinutes() / 60) * ALTURA_HORA_PX;

    // Helper: construye grupos de solape y asigna columnas por grupo
    const buildColumnLayout = (eventos) => {
      // ordenar por inicio, y en caso de empate por fin
      const sorted = [...eventos].sort((a, b) =>
        a.horaInicio === b.horaInicio ? a.horaFin - b.horaFin : a.horaInicio - b.horaInicio
      );

      const groups = []; // [{events: [evt], cols: Map(evtId -> colIndex), colsCount}]
      let active = [];   // eventos que siguen abiertos dentro del grupo actual (por fin)

      let currentGroup = { events: [], cols: new Map(), colsCount: 0 };

      const flushGroup = () => {
        if (currentGroup.events.length === 0) return;

        // asignación greedy de columnas solo para los eventos de este grupo
        const columnasOcupadasHasta = []; // por columna, el fin del último evento que la ocupa
        currentGroup.events
          .sort((a, b) =>
            a.horaInicio === b.horaInicio ? a.horaFin - b.horaFin : a.horaInicio - b.horaInicio
          )
          .forEach((ev) => {
            let col = 0;
            while (
              columnasOcupadasHasta[col] !== undefined &&
              ev.horaInicio < columnasOcupadasHasta[col]
            ) {
              col++;
            }
            currentGroup.cols.set(ev.id, col);
            columnasOcupadasHasta[col] = ev.horaFin;
          });

        currentGroup.colsCount = Math.max(1, columnasOcupadasHasta.length);
        groups.push(currentGroup);
        currentGroup = { events: [], cols: new Map(), colsCount: 0 };
      };

      sorted.forEach((ev) => {
        // limpiar del conjunto activo los que ya terminaron
        active = active.filter((a) => a.horaFin > ev.horaInicio);

        if (active.length === 0 && currentGroup.events.length > 0) {
          // no hay solapes con el grupo actual → cerrarlo
          flushGroup();
        }

        // añadir al grupo y al activo
        currentGroup.events.push(ev);
        active.push(ev);
      });

      // último grupo pendiente
      flushGroup();

      // construir un índice: eventoId -> {groupColsCount, colIndex}
      const placement = new Map();
      groups.forEach((g) => {
        g.events.forEach((ev) => {
          placement.set(ev.id, {
            colsCount: g.colsCount,
            colIndex: g.cols.get(ev.id) ?? 0,
          });
        });
      });

      return placement;
    };

    return (
      <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
        <div
          className="grid relative bg-white"
          style={{
            gridTemplateColumns: 'auto repeat(7, minmax(0, 1fr))',
            gridTemplateRows: `auto repeat(24, ${ALTURA_HORA_PX}px)`,
          }}
        >
          {/* Header de días */}
          {semana.map((fecha, diaIndex) => (
            <div
              key={fecha.toISOString()}
              className={`sticky top-0 p-1 sm:p-2 text-center z-20 bg-white border-b border-r border-gray-200 ${
                fecha.toDateString() === now.toDateString() ? 'bg-blue-50' : ''
              }`}
              style={{ gridColumn: diaIndex + 2, gridRow: 1 }}
            >
              <span className="sm:hidden font-semibold text-gray-500">
                {diasSemanaUnaLetra[fecha.getDay()]}
              </span>
              <span className="hidden sm:inline text-sm capitalize text-gray-600">
                {['domingo','lunes','martes','miércoles','jueves','viernes','sábado'][fecha.getDay()].substring(0, 3)}
              </span>
              <span
                className={`block sm:inline-block sm:ml-2 font-bold ${
                  fecha.toDateString() === now.toDateString() ? 'text-blue-600' : 'text-gray-800'
                }`}
              >
                {fecha.getDate().toString().padStart(2, '0')}
              </span>
            </div>
          ))}

          {/* Columna de horas */}
          <div className="sticky top-0 left-0 z-30 bg-white border-b border-r border-gray-200" style={{ gridColumn: 1, gridRow: 1 }} />
          {horas.map((hora) => (
            <div
              key={hora}
              className="sticky left-0 text-[11px] leading-4 text-gray-500 text-right pr-2 border-r border-t border-gray-200 bg-white z-10"
              style={{ gridColumn: 1, gridRow: hora + 2 }}
            >
              {`${hora.toString().padStart(2, '0')}:00`}
            </div>
          ))}

          {/* Celdas y eventos */}
          {semana.map((diaActual, diaIndex) => {
            const eventosDelDia = eventosDelCalendario
              .filter((e) => e.fecha.toDateString() === diaActual.toDateString())
              .map((e) => ({ ...e, id: e.id.toString() })); // asegurar string id

            // Calcular layout por grupos de solape
            const placement = buildColumnLayout(eventosDelDia);

            return (
              <div
                key={diaIndex}
                className="relative border-r border-gray-200"
                style={{ gridColumn: diaIndex + 2, gridRow: '2 / span 24' }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top;
                  const h = Math.min(23, Math.max(0, Math.floor(y / ALTURA_HORA_PX)));
                  setCurrentDate(new Date(diaActual.setHours(h, 0, 0, 0)));
                }}
              >
                {/* Rejilla de horas */}
                {horas.map((hora) => (
                  <div key={hora} className="border-t border-gray-200" style={{ height: `${ALTURA_HORA_PX}px` }} />
                ))}

                {/* Eventos render */}
                {eventosDelDia.map((evento) => {
                  const pos = placement.get(evento.id) || { colsCount: 1, colIndex: 0 };
                  const duracion = Math.max(1 / 3, evento.horaFin - evento.horaInicio);
                  const top = evento.horaInicio * ALTURA_HORA_PX;
                  const height = duracion * ALTURA_HORA_PX - 2;
                  const width = `calc(${100 / pos.colsCount}% - 4px)`;
                  const left = `calc(${(100 / pos.colsCount) * pos.colIndex}% + 2px)`;

                  return (
                    <div
                      key={evento.id}
                      className={`${evento.colorInfo.bg} text-white rounded p-1 absolute cursor-pointer text-[11px] leading-4 overflow-hidden shadow-sm`}
                      style={{ top, height, width, left, padding: '2px 4px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onServicioClick && onServicioClick(evento);
                      }}
                      title={`${evento.titulo} (${evento.cliente || ''})`}
                    >
                      <p className="font-semibold truncate">
                        {evento.codigo} {evento.titulo}
                      </p>
                      <p className="opacity-90 truncate">{`${evento.horaInicio
                        .toString()
                        .padStart(2, '0')}:00 - ${evento.horaFin.toString().padStart(2, '0')}:00`}</p>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Línea de hora actual */}
          {hoyIndex !== -1 && (
            <div
              className="relative h-0.5 bg-red-500 z-10"
              style={{
                top: `${posicionHoraActual}px`,
                gridColumn: hoyIndex + 2,
                gridRow: '2 / span 24',
              }}
            >
              <div className="absolute -left-1.5 -top-1 w-3 h-3 rounded-full bg-red-500" />
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ========= VISTA DÍA ========= */
  const VistaDia = () =>
    subVistaDia === 'lista' ? <VistaDiaLista /> : <VistaDiaCalendario />;

  const VistaDiaLista = () => {
    const eventosDelDia = eventosDelCalendario
      .filter((e) => e.fecha.toDateString() === currentDate.toDateString())
      .sort((a, b) => a.horaInicio - b.horaInicio);

    return (
      <div className="p-2 md:p-4 space-y-2">
        {eventosDelDia.map((evento) => (
          <div
            key={evento.id}
            className={`${evento.colorInfo.bg} text-white rounded-lg p-3 flex justify-between items-center cursor-pointer overflow-hidden`}
            onClick={() => onServicioClick && onServicioClick(evento)}
            title={`${evento.titulo} (${evento.cliente || ''})`}
          >
            <div className="min-w-0">
              <p className="text-xs opacity-75 truncate">{evento.codigo} - {evento.nombreEmpresa}</p>
              <p className="font-semibold truncate">{evento.titulo}</p>
              <p className="text-sm opacity-90 truncate">{evento.cliente}</p>
            </div>
            <div className="text-right flex items-center space-x-2 md:space-x-4">
              <span className="font-medium text-xs md:text-sm">{`${evento.horaInicio.toString().padStart(2, '0')}:00 - ${evento.horaFin.toString().padStart(2, '0')}:00`}</span>
              <button className="bg-white/20 hover:bg-white/40 p-2 rounded-full">
                <Eye size={20} />
              </button>
            </div>
          </div>
        ))}
        {eventosDelDia.length === 0 && <p className="text-center text-gray-500 py-8">No hay servicios para este día.</p>}
      </div>
    );
  };

  const VistaDiaCalendario = () => {
    const horas = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
    const ALTURA_HORA = 60;
    const esHoy = currentDate.toDateString() === now.toDateString();
    const posicionHoraActual = (now.getHours() + now.getMinutes() / 60) * ALTURA_HORA;

    const eventosDelDia = eventosDelCalendario.filter((e) => e.fecha.toDateString() === currentDate.toDateString());
    const eventosConPosicion = [];
    eventosDelDia.sort((a, b) => a.horaInicio - b.horaInicio).forEach((evento) => {
      let columna = 0;
      while (eventosConPosicion.some((e) => e.columna === columna && evento.horaInicio < e.evento.horaFin && evento.horaFin > e.evento.horaInicio)) {
        columna++;
      }
      eventosConPosicion.push({ evento, columna });
    });
    let maxColumnasSimultaneas = 0;
    for (let i = 0; i < 24; i++) {
      const eventosEnHora = eventosConPosicion.filter((e) => i >= e.evento.horaInicio && i < e.evento.horaFin);
      if (eventosEnHora.length > maxColumnasSimultaneas) maxColumnasSimultaneas = eventosEnHora.length;
    }
    maxColumnasSimultaneas = Math.max(1, maxColumnasSimultaneas);

    return (
      <div className="flex overflow-y-auto" style={{ maxHeight: '70vh' }}>
        <div className="w-20 text-sm text-gray-500 flex-shrink-0">
          {horas.map((hora) => (
            <div key={hora} style={{ height: `${ALTURA_HORA}px` }} className="text-right pr-4 border-r border-t border-gray-200 -mt-px pt-1">
              {hora}
            </div>
          ))}
        </div>
        <div
          className="flex-1 relative"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const h = Math.min(23, Math.max(0, Math.floor(y / ALTURA_HORA)));
            setCurrentDate(new Date(currentDate.setHours(h, 0, 0, 0)));
          }}
        >
          {horas.map((_, i) => (
            <div key={i} style={{ height: `${ALTURA_HORA}px` }} className="border-b border-r border-gray-200" />
          ))}
          {esHoy && (
            <div className="absolute w-full h-0.5 bg-red-500 z-10" style={{ top: `${posicionHoraActual}px` }}>
              <div className="absolute -left-1.5 -top-1 w-3 h-3 rounded-full bg-red-500"></div>
            </div>
          )}
          {eventosConPosicion.map(({ evento, columna }) => {
            const duracion = Math.max(1 / 3, evento.horaFin - evento.horaInicio);
            const top = evento.horaInicio * ALTURA_HORA;
            const height = duracion * ALTURA_HORA - 2;
            const width = `calc(${100 / maxColumnasSimultaneas}% - 6px)`;
            const left = `calc(${(100 / maxColumnasSimultaneas) * columna}% + 3px)`;
            return (
              <div
                key={evento.id}
                className={`${evento.colorInfo.bg} text-white rounded p-2 absolute cursor-pointer flex flex-col justify-center overflow-hidden z-[5] shadow-sm`}
                style={{ top, height, width, left }}
                onClick={(e) => { e.stopPropagation(); onServicioClick && onServicioClick(evento); }}
                title={`${evento.titulo} (${evento.cliente || ''})`}
              >
                <p className="text-xs opacity-75 truncate">{evento.codigo} - {evento.nombreEmpresa}</p>
                <p className="font-bold text-sm truncate">{evento.titulo}</p>
                <p className="text-xs truncate">{evento.cliente}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ========= Contenedor ========= */
  return (
    <div className="bg-white rounded-xl border border-gray-200/75 max-w-7xl mx-auto my-4 font-sans shadow-lg shadow-gray-400/10 overflow-hidden">
      <CalendarHeader />
      <div className="max-h-[75vh] overflow-auto">
        {vista === 'mes' && <VistaMes />}
        {vista === 'semana' && <VistaSemana />}
        {vista === 'dia' && <VistaDia />}
      </div>

      {seleccion && (
        <div className="px-4 py-2 border-t bg-amber-50 text-amber-900 text-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-amber-400 text-white font-bold">
              {seleccion.date.getDate()}
            </span>
            <span className="capitalize">
              Seleccionaste: {['domingo','lunes','martes','miércoles','jueves','viernes','sábado'][seleccion.date.getDay()]} {seleccion.date.getDate()}
            </span>
          </div>
          <button onClick={limpiarSeleccion} className="text-xs underline hover:opacity-80">
            Limpiar
          </button>
        </div>
      )}
    </div>
  );
});

export default CalendarioWidget;
